import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { selectBarrierDetails } from '../../../store/dataSelectors';
import { loadBarrierNameDetails } from '../../../store/directusService';
import { updatePreSelects, updateSelectedItems } from '../../../utils/directusFunctions';
import { NO_CONTRACT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import { BARRIER_NAME_DETAILS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { hasRole, LOCAL_ADMIN, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import NotFound from '../../notFound/NotFound';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateBarriersAdmin';

import form from '../../../commonStyles/formStyles.module.css';
import admin from '../adminStyles.module.css';

const acceptedRoles = [SUPERUSER];
const viewRoles = [LOCAL_ADMIN, SUPERUSER];
const _BARRIER_NAME = 'barrier_name';
const _MESSAGE = 'Updating barrier_name collection';

const BarriersAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [selectedBarrierNameIds, setSelectedBarrierNameIds] = useState([]);
    const [preSelectedBarrierNames, setPreSelectedBarrierNames] = useState([]);
    const [preSelectedBarrierNameIds, setPreSelectedBarrierNameIds] = useState([]);
    const [errors, setErrors] = useState({});
    const [isDisabled, setIsDisabled] = useState(true);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const barrierNameDetails = useSelector(selectBarrierDetails);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        if (contractId) dispatch(loadBarrierNameDetails());
    }, [contractId]);

    useEffect(() => {
        setIsDisabled(!hasRole(acceptedRoles, roles));
    }, [roles]);

    useEffect(() => {
        if (barrierNameDetails?.length) {
            const { preSelects, preSelectIds } = updatePreSelects(
                _BARRIER_NAME,
                barrierNameDetails,
                contractId
            );
            setPreSelectedBarrierNames(preSelects);
            setPreSelectedBarrierNameIds(preSelectIds);
        }
    }, [barrierNameDetails]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage.includes(_MESSAGE)) dispatch(loadBarrierNameDetails());
        setIsButtonDisabled(false);
    }, [successMessage]);

    // FNS
    const loadingError = () => {
        if (!contractId) return NO_CONTRACT_ERROR;
        if (barrierNameDetails?.length < 1) return BARRIER_NAME_DETAILS_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onBarrierNameChange = (chosenIds) => {
        clearError('barrierNames');
        setSelectedBarrierNameIds(chosenIds);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(preSelectedBarrierNameIds, selectedBarrierNameIds);
        if (Object.keys(newErrors)?.length) {
            setErrors(newErrors);
            return;
        }

        updateSelectedItems(
            _BARRIER_NAME,
            barrierNameDetails,
            selectedBarrierNameIds,
            contractId,
            _BARRIER_NAME,
            dispatch
        );
    };

    // RENDER
    if (!hasRole(viewRoles, roles)) return <NotFound />;
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <form
            className={form.formWrapper}
            onSubmit={onSubmit}
            data-testid="barrier_admin_form_start">
            <MultiSelect
                id="barrierNames"
                label="Barrier Names"
                placeholder="Select barrier names..."
                disabled={isDisabled}
                mandatory={true}
                menuItems={barrierNameDetails || []}
                preSelectedIds={preSelectedBarrierNameIds}
                preSelects={preSelectedBarrierNames}
                error={errors.barrierNames}
                onChange={(chosenIds) => onBarrierNameChange(chosenIds)}
            />

            <Button
                id="barrierAdminButton"
                content="UPDATE PARTICIPANT BARRIERS"
                disabled={isDisabled || isButtonDisabled}
                customErrorClass="reduceTopMargin"
                error={errors.button}
                clearError={() => clearError('button')}
            />

            <div className={admin.titlesBlockWrapper}>
                <p>Priority</p>
                <p>Start Date</p>
                <p>End Date</p>
                <p>Status</p>
            </div>
        </form>
    );
};

BarriersAdmin.propTypes = {
    contractId: PropTypes.string
};

export default BarriersAdmin;
